/**
 * Eva Platform Defaults page (SF-3839).
 *
 * The single place to set the GLOBAL default monthly Eva caps — the per-company
 * default cap and the per-user default cap — that apply when no more-specific
 * override exists. sf-api owns these values, publishes them to the shared Valkey
 * keys (eva:budget:company:default / eva:budget:user:default), and
 * sf-chat-service enforces them, so the number shown here IS the number enforced
 * (no more drift between the admin UI and what the chat service applies).
 *
 * Before SF-3839 the "default" lived in two unsynchronised env vars (sf-api for
 * display, sf-chat-service for enforcement) that could disagree. This tab makes
 * the default admin-settable with no redeploy.
 *
 * Reachable by operators holding `admin:eva:platform:read` — platform-wide
 * defaults affect every company, so this is an SF-internal control gated on the
 * super-admin-only `admin:eva:platform:*` scope (super-admins inherit it via the
 * `admin:eva:*` wildcard). Writes additionally require `admin:eva:platform:write`
 * server-side (defence in depth); read-only viewers see disabled inputs.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Popconfirm,
  Result,
  Row,
  Skeleton,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DollarOutlined, TeamOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';

import { hasPermission } from '../../utils/permissions';
import { useCanEditCompany } from '../companies/api/editableCompaniesClient';
import { formatCurrency, EM_DASH, parseMoney } from '../../utils/format';
import { formatShortDate } from '../../utils/date';
import {
  EVA_PLATFORM_READ_PERMISSION,
  EVA_PLATFORM_WRITE_PERMISSION,
  platformDefaultsUpdateMutationArgs,
  useEvaBudgetPlatformDefaults,
  useSetEvaBudgetPlatformDefaults,
  type EvaBudgetPlatformDefaults,
} from './api/evaPlatformDefaultsClient';

const { Title, Text } = Typography;

/** Pull a human-readable message out of an sf-api axios error (incl. 403). */
function errorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  if (axiosErr?.response?.status === 403) {
    return 'You do not have permission to change the Eva platform defaults.';
  }
  return axiosErr?.response?.data?.message || fallback;
}

export const EvaPlatformDefaultsPage: React.FC = () => {
  // Platform-wide defaults are an SF-internal control gated on the
  // `admin:eva:platform:read` PERMISSION (super-admins inherit it via the
  // `admin:eva:*` wildcard) — not a bare super-admin role check. This keeps the
  // UI gate byte-identical to the sf-api requirePermissions enforcement, so the
  // nav entry, the page, and the server can never drift. Writes additionally
  // require `admin:eva:platform:write`.
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const perms = permissions ?? [];
  const canRead = hasPermission(perms, EVA_PLATFORM_READ_PERMISSION);
  // SF-4668: platform defaults affect every company, so staff with a company list can only view them.
  const { canEditPlatform } = useCanEditCompany(undefined);
  const canWrite = hasPermission(perms, EVA_PLATFORM_WRITE_PERMISSION) && canEditPlatform;

  // Only fire the GET once permissions have resolved and the operator holds
  // platform:read (avoid a doomed request / denial flash).
  const defaultsQuery = useEvaBudgetPlatformDefaults({
    enabled: !permsLoading && canRead,
  });
  const defaults: EvaBudgetPlatformDefaults | undefined = defaultsQuery.data?.data;
  const setDefaults = useSetEvaBudgetPlatformDefaults();

  const loadedCompanyCap = parseMoney(defaults?.companyMonthlyCapUsd);
  const loadedUserCap = parseMoney(defaults?.userMonthlyCapUsd);

  // Local edit state, seeded from the loaded values. Save enables only when an
  // edited value differs from what loaded.
  const [companyCap, setCompanyCap] = useState<number | null>(null);
  const [userCap, setUserCap] = useState<number | null>(null);
  const [note, setNote] = useState<string>('');

  // Re-seed the inputs whenever fresh data lands (initial load or post-save
  // refetch) so the "dirty" comparison is against the persisted values.
  useEffect(() => {
    setCompanyCap(loadedCompanyCap);
    setUserCap(loadedUserCap);
    setNote('');
    // defaults identity changes on every (re)fetch; the caps are derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults]);

  const isDirty = useMemo(
    () => companyCap !== loadedCompanyCap || userCap !== loadedUserCap,
    [companyCap, userCap, loadedCompanyCap, loadedUserCap],
  );

  const saving = setDefaults.isLoading;

  const handleSave = () => {
    // Both caps must be STRICTLY POSITIVE finite numbers. These are platform
    // DEFAULTS, and a 0 default is invalid on two counts: (1) the sf-api writer
    // (evaBudgetPlatformDefaultService.validateCapField) rejects `<= 0` with a
    // 400 ("must be a positive finite number") — a global 0 would mean "block
    // every company / every user"; (2) the chat-service reader honours a default
    // key only when it parses to `> 0`, so a published 0 would be silently
    // ignored and fall through to the in-process failsafe. Rejecting it here
    // gives an immediate, clear message instead of a round-trip 400. (Per-entity
    // OVERRIDES legitimately still allow 0 as a hard stop — that's a different
    // control; this strict `> 0` is correct ONLY for defaults.)
    if (
      companyCap === null ||
      userCap === null ||
      !Number.isFinite(companyCap) ||
      !Number.isFinite(userCap) ||
      companyCap <= 0 ||
      userCap <= 0
    ) {
      message.error('Enter positive company and per-user monthly caps (must be greater than $0).');
      return;
    }
    const trimmedNote = note.trim();
    setDefaults.mutate(
      platformDefaultsUpdateMutationArgs({
        companyMonthlyCapUsd: companyCap,
        userMonthlyCapUsd: userCap,
        ...(trimmedNote ? { note: trimmedNote } : {}),
      }),
      {
        onSuccess: () => {
          message.success('Eva platform defaults updated.');
          defaultsQuery.refetch?.();
        },
        onError: (error) => {
          message.error(errorMessage(error, 'Failed to update the Eva platform defaults.'));
        },
      },
    );
  };

  const writeDisabledHint = canWrite
    ? undefined
    : !canEditPlatform
      ? 'These defaults affect every company, so only staff without a company list can change them.'
      : 'You do not have permission to change the Eva platform defaults (admin:eva:platform:write).';

  // --- Permission gate: resolve before rendering anything sensitive. ---
  if (permsLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }
  if (!canRead) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="Eva platform defaults require the admin:eva:platform:read permission."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Space align="center">
          <Title level={3} style={{ marginBottom: 0 }}>
            Eva — Platform Defaults
          </Title>
          <Tag color="purple">Super-admins</Tag>
        </Space>
        <div>
          <Text type="secondary">
            Global default monthly Eva caps applied when no per-company or per-user override is set.
            These are the single source of truth — the value here is exactly what is enforced.
          </Text>
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        message="How these defaults are used"
        description={
          <>
            The <strong>company default</strong> caps each company&rsquo;s total monthly Eva spend;
            the <strong>per-user default</strong> caps each individual user. A per-company or
            per-user override (set on the company / user pages) takes precedence over these
            defaults. Super-admins are never budget-limited. Changes take effect without a redeploy.
          </>
        }
      />

      {defaultsQuery.isLoading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 2 }} title={{ width: '40%' }} />
        </Card>
      ) : defaultsQuery.isError ? (
        <Card>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text type="danger">Could not load the Eva platform defaults. Please retry.</Text>
            <Button size="small" onClick={() => defaultsQuery.refetch?.()}>
              Retry
            </Button>
          </Space>
        </Card>
      ) : (
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    <DollarOutlined /> Company Monthly Default Cap
                  </Text>
                  <Tooltip title={writeDisabledHint}>
                    {/* Wrap in an inline-block span: AntD disabled controls
                        don't emit pointer events, so without this the
                        write-permission tooltip would never show on hover. */}
                    <span style={{ display: 'inline-block' }}>
                      <InputNumber
                        aria-label="Company monthly default cap (USD)"
                        value={companyCap}
                        min={0}
                        step={50}
                        disabled={!canWrite || saving}
                        prefix="$"
                        style={{ width: 220 }}
                        onChange={(next) => setCompanyCap(typeof next === 'number' ? next : null)}
                      />
                    </span>
                  </Tooltip>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {loadedCompanyCap !== null
                      ? `Current: ${formatCurrency(loadedCompanyCap)} / month`
                      : EM_DASH}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} md={12}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text type="secondary">
                    <TeamOutlined /> Per-User Monthly Default Cap
                  </Text>
                  <Tooltip title={writeDisabledHint}>
                    {/* Wrap in an inline-block span: AntD disabled controls
                        don't emit pointer events, so without this the
                        write-permission tooltip would never show on hover. */}
                    <span style={{ display: 'inline-block' }}>
                      <InputNumber
                        aria-label="Per-user monthly default cap (USD)"
                        value={userCap}
                        min={0}
                        step={5}
                        disabled={!canWrite || saving}
                        prefix="$"
                        style={{ width: 220 }}
                        onChange={(next) => setUserCap(typeof next === 'number' ? next : null)}
                      />
                    </span>
                  </Tooltip>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {loadedUserCap !== null
                      ? `Current: ${formatCurrency(loadedUserCap)} / month`
                      : EM_DASH}
                  </Text>
                </Space>
              </Col>
            </Row>

            {canWrite && (
              <Space direction="vertical" size={4} style={{ width: '100%', maxWidth: 460 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Note (optional — recorded in the change audit)
                </Text>
                <Input
                  aria-label="Change note"
                  value={note}
                  maxLength={280}
                  placeholder="e.g. Raised stage defaults for QA"
                  disabled={saving}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Space>
            )}

            <Space wrap align="center" size="middle">
              {canWrite && (
                <Popconfirm
                  title="Update Eva platform defaults?"
                  description={
                    <span>
                      This changes the global default caps for{' '}
                      <strong>every company and user</strong> without a per-company / per-user
                      override. It takes effect immediately, with no redeploy.
                    </span>
                  }
                  okText="Update defaults"
                  cancelText="Cancel"
                  okButtonProps={{ loading: saving }}
                  disabled={!isDirty || saving}
                  onConfirm={handleSave}
                >
                  <Button type="primary" loading={saving} disabled={!isDirty}>
                    Save defaults
                  </Button>
                </Popconfirm>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {defaults?.updatedAt
                  ? `Last changed ${formatShortDate(defaults.updatedAt)}${
                      defaults.updatedBy ? ` by ${defaults.updatedBy}` : ''
                    }${defaults.note ? ` — ${defaults.note}` : ''}`
                  : 'Seeded from environment defaults'}
              </Text>
            </Space>
          </Space>
        </Card>
      )}
    </Space>
  );
};
